import { useEffect, useRef } from 'react';
import { cn } from 'v5/platform/dom/cn';

/** A 12 px cell with a 10 px tile and 2 px grout. */
const CELL = 12;
const TILE_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><rect x="1" y="1" width="10" height="10" rx="1.5" fill="#000"/></svg>'
)}")`;
/** Noise features span about a third of the width, whatever the width is. */
const NOISE_PER_WIDTH = 3.6 * CELL;
/**
 * Each tile twinkles on its own clock, between these many seconds apart, and
 * spends this share of its cycle flaring up and fading back.
 */
const TWINKLE_MIN_S = 2.5;
const TWINKLE_SPREAD_S = 4;
const TWINKLE_ON = 0.45;
/** How likely a tile flares in a cycle: rarely in the dark, often in the aurora. */
const TWINKLE_RARE = 0.06;
const TWINKLE_IN_AURORA = 0.5;
/** How far a flare lifts a tile towards full light: a soft glint, not a spark. */
const TWINKLE_PEAK = 0.55;
/** Redraws a second: enough that a tile's brightness glides rather than ticks. */
const FPS = 30;

/**
 * Tiles in the text colour lit by a slow aurora and twinkling one by one: a
 * calm noise field sets each tile's half-tone, and random tiles flare up and
 * fade back on their own clocks, more often where the aurora is bright; the
 * light grows towards the bottom. Faint (`opacity`, .14 by default via
 * `--fx-intensity`); reduced motion keeps it still. Decorative. Fills the
 * nearest positioned ancestor.
 */
export function TileAurora({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useAuroraTiles(canvasRef);
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 overflow-hidden',
        className
      )}
    >
      <canvas
        ref={canvasRef}
        className="absolute left-0 top-0 [image-rendering:pixelated]"
        style={{
          opacity: 'var(--fx-intensity, .14)',
          maskImage: TILE_MASK,
          maskSize: `${CELL}px ${CELL}px`
        }}
      />
    </div>
  );
}

/**
 * Draws one pixel per tile and lets the GPU scale it up under the tile mask,
 * so a frame costs one noise sample per tile and no paths. Every frame reads
 * the host's size, so a resize needs no observer.
 */
function useAuroraTiles(canvasRef: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    const probe = document
      .createElement('canvas')
      .getContext('2d', { willReadFrequently: true });
    const host = canvas?.parentElement;
    if (!canvas || !context || !probe || !host) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)');
    // Canvas reads any CSS colour; one probe pixel turns it into RGB.
    const rgbOf = (css: string) => {
      probe.clearRect(0, 0, 1, 1);
      probe.fillStyle = css;
      probe.fillRect(0, 0, 1, 1);
      return probe.getImageData(0, 0, 1, 1).data;
    };
    const start = performance.now();
    let timer = 0;
    let frame = 0;

    const draw = (now: number) => {
      frame = 0;
      // Reduced motion: the aurora stands still.
      const drift = reduce.matches ? 0 : (now - start) / 1000;
      const cols = Math.max(1, Math.ceil(host.clientWidth / CELL));
      const rows = Math.max(1, Math.ceil(host.clientHeight / CELL));
      if (canvas.width !== cols || canvas.height !== rows) {
        canvas.width = cols;
        canvas.height = rows;
        canvas.style.width = `${cols * CELL}px`;
        canvas.style.height = `${rows * CELL}px`;
      }
      const [r, g, b] = rgbOf(getComputedStyle(host).color);
      const image = context.createImageData(cols, rows);
      const scale = NOISE_PER_WIDTH / (cols * CELL);
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          // Slow enough to read as a lit sky, not a pattern sliding behind a grille.
          const n = fbm(
            col * scale + drift * 0.025,
            row * scale * 0.6 - drift * 0.015
          );
          // A wide, smooth ramp: most tiles sit in half-tones, few reach full light.
          const ramp = Math.min(1, Math.max(0, (n - 0.28) / 0.5));
          const aurora = ramp * ramp * (3 - 2 * ramp);
          const lit =
            aurora * 0.5 +
            twinkle(col, row, drift, aurora) *
              TWINKLE_PEAK *
              (1 - aurora * 0.5);
          const i = (row * cols + col) * 4;
          image.data[i] = r;
          image.data[i + 1] = g;
          image.data[i + 2] = b;
          image.data[i + 3] =
            255 * (0.06 + 0.64 * lit * (0.4 + 0.6 * (row / rows)));
        }
      }
      context.putImageData(image, 0, 0);
      schedule();
    };
    const schedule = () => {
      if (timer || frame) return;
      timer = window.setTimeout(() => {
        timer = 0;
        frame = requestAnimationFrame(draw);
      }, 1000 / FPS);
    };
    draw(start);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [canvasRef]);
}

/**
 * How far a tile has flared at `time`, 0–1: in each of its cycles it either
 * stays dark or rises and falls once, smoothly; whether it does is drawn anew
 * every cycle, so the stars never repeat in place.
 */
function twinkle(col: number, row: number, time: number, aurora: number) {
  const period = TWINKLE_MIN_S + TWINKLE_SPREAD_S * hash(col + 0.5, row - 3.7);
  const t = time / period + hash(col - 9.1, row + 2.3);
  const cycle = Math.floor(t);
  const chance = TWINKLE_RARE + TWINKLE_IN_AURORA * aurora;
  if (hash(col * 1.37 + cycle * 19.19, row * 0.73 + cycle * 7.77) > chance) {
    return 0;
  }
  const phase = (t - cycle) / TWINKLE_ON;
  if (phase >= 1) return 0;
  const rise = Math.sin(Math.PI * phase);
  return rise * rise;
}

function hash(x: number, y: number) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function valueNoise(x: number, y: number) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi);
  const b = hash(xi + 1, yi);
  const c = hash(xi, yi + 1);
  const d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fbm(x: number, y: number) {
  return (
    valueNoise(x, y) * 0.6 +
    valueNoise(x * 2, y * 2) * 0.3 +
    valueNoise(x * 4, y * 4) * 0.1
  );
}
